import { Link2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import {
  DOUBLE_NIGHT_LABEL,
  FAMILY_CELL_LABEL,
  doubleStripes,
  familyStripes,
} from './CalendarDayCells';

export const PLAN_MARK_LABEL = 'Geplante vorzeitige Abreise';

interface Props {
  family: boolean;
  doubleBooked: boolean;
  plan: boolean;
  /** Ziehen und freie Tage gibt es nur für den Betrieb. */
  manager: boolean;
  /** Handy: nur die Zeichen; Schräge und Ziehen erklärt die Hinweiszeile nicht. */
  compact?: boolean;
}

/**
 * Eine Legende unter der Belegung (Board „Belegung“): jedes Zeichen in der Form, die es erklärt,
 * und nur, wenn es gerade zu sehen ist – die Schräge erklärt sie immer, weil sie jeder Wechseltag
 * zeigt.
 */
export function CalendarLegend({ family, doubleBooked, plan, manager, compact = false }: Props) {
  if (compact && !family && !doubleBooked && !plan) return null;
  return (
    <div className="text-ink-soft flex flex-wrap items-center gap-x-4 gap-y-2 px-1 text-xs">
      {!compact && (
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="flex">
            <span className="bg-navy h-3 w-[30px] [clip-path:polygon(0_0,100%_0,calc(100%_-_6px)_100%,0_100%)]" />
            <span className="bg-forest -ml-1 h-3 w-[30px] [clip-path:polygon(6px_0,100%_0,100%_100%,0_100%)]" />
          </span>
          Schräge nur, wenn am selben Tag einer geht und einer kommt
        </span>
      )}
      {family && (
        <span className="flex items-center gap-1.5">
          <Swatch className={familyStripes}>
            <Link2 size={11} className="text-ink-faint" />
          </Swatch>
          {FAMILY_CELL_LABEL}
        </span>
      )}
      {doubleBooked && (
        <span className="flex items-center gap-1.5">
          <Swatch className={cn(doubleStripes, 'border-sealwax/30 border')} />
          {DOUBLE_NIGHT_LABEL}
          <span className="text-ink-faint">
            – zwei Buchungen oder Buchung und Verbund in einer Nacht
          </span>
        </span>
      )}
      {plan && (
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="bg-navy flex h-3.5 w-6 justify-center rounded-sm">
            <span className="border-amber-on-dark border-l-2 border-dashed" />
          </span>
          {PLAN_MARK_LABEL}
        </span>
      )}
      {manager && !compact && (
        <span>
          Balken auf eine andere Zeile ziehen: Apartment wechseln, mit Vorschau · Freien Tag
          klicken: manuelle Buchung
        </span>
      )}
    </div>
  );
}

function Swatch({ className, children }: { className: string; children?: React.ReactNode }) {
  return (
    <span aria-hidden className={cn('grid h-3.5 w-6 place-items-center rounded-sm', className)}>
      {children}
    </span>
  );
}

/**
 * Gestrichelte Marke am Beginn des geplanten Abreisetags; Balken und Klicks bleiben darunter frei.
 * Für Vorleser trägt der Balken den Plan im Namen, die Legende erklärt das Zeichen.
 */
export function CalendarPlanMark({ column }: { column: number }) {
  return (
    <span
      aria-hidden
      data-plan-mark
      style={{ gridColumn: column + 2, gridRow: 1 }}
      className="border-amber-on-dark pointer-events-none relative z-20 my-1.5 border-l-2 border-dashed"
    />
  );
}
